/**
 * [ui1006, paczka KIT] KSZTAŁT REJESTRU OBSZARÓW — wspólne stałe czterech rejestrów (`wykazKsc.js`, `procedury.js`,
 * `integracje.js`, `office365.js`).
 *
 * Polecenie właściciela z 5.10.2026: cztery strony panelu „nie są podzielone, są naćkane, nie do ogarnięcia”. Każda z nich
 * dostaje tę samą ramę: GRUPY w jedynym pasku i MIEJSCA (w kodzie: obszary) w rzędzie pod nim. Rejestr jest jedynym źródłem
 * tej nawigacji dla strony — czytają go powłoka, start, „Powrót” w nagłówku aplikacji, przekierowania starych adresów,
 * ekran linku na telefonie i zapadki tras.
 *
 * Słowo w kodzie a słowo na ekranie: w kodzie jednostka nawigacji to „obszar”, NA EKRANIE to „miejsce” („Wszystkie miejsca”,
 * „Szukaj miejsca…”) — „obszar” jest pojęciem modułu Procedur (obszar ustawy, normy).
 *
 * Ten plik nie zna Reacta ani tras — same stałe, żeby rejestry, czyste funkcje adresów i zapadki liczyły z jednego miejsca.
 */

/** Identyfikatory trzech grup wspólnych dla czterech stron (pierwsza, druga i ostatnia w pasku — zapadka N-17). */
export const GRUPA_STARTU = "do-zalatwienia";
export const GRUPA_PRACY = "praca";
export const GRUPA_USTAWIEN = "ustawienia";

/** Identyfikator obszaru startowego („Do załatwienia”) — ten sam na każdej stronie. */
export const OBSZAR_STARTU = "start";

/** Etykiety wspólne (2.8 projektu: jedna rzecz — jedna nazwa na czterech stronach). */
export const ETYKIETA_STARTU = "Do załatwienia";
export const ETYKIETA_PRACY = "Praca";
export const ETYKIETA_USTAWIEN = "Ustawienia";
export const ETYKIETA_URUCHOMIENIA = "Uruchomienie";

/** Segment adresu grupy „Ustawienia” — jej obszary mają dwa segmenty: `…/ustawienia/<obszar>`, EN `…/settings/<area>`. */
export const SEGMENT_USTAWIEN = Object.freeze({ pl: "ustawienia", en: "settings" });

export const grupaStartu = () => ({ id: GRUPA_STARTU, etykieta: ETYKIETA_STARTU });
export const grupaPracy = () => ({ id: GRUPA_PRACY, etykieta: ETYKIETA_PRACY });
export const grupaUstawien = () => ({ id: GRUPA_USTAWIEN, etykieta: ETYKIETA_USTAWIEN, segment: SEGMENT_USTAWIEN });

/**
 * Tymczasowa treść obszaru — do czasu, aż paczka strony podmieni `komponent` na własny plik i przełączy `aktywna: true`.
 * Jedna funkcja dla wszystkich obszarów, żeby budowanie miało jeden leniwy kawałek zamiast siedemdziesięciu.
 */
export const wBudowie = () => import("../../components/common/Obszary/ObszarWBudowie");

/**
 * Jeden obszar rejestru z wartościami domyślnymi — rejestry podają tylko to, czym obszar się wyróżnia.
 *
 * Pola (kształt z 2.3 projektu):
 *   id          — stały identyfikator obszaru na stronie (unikalny; argument `adresObszaru(strona, id)`),
 *   grupa       — identyfikator grupy z `grupy`,
 *   segment     — { pl, en } OSTATNI segment adresu (obszar grupy „Ustawienia” dostaje przed nim segment grupy); start: null,
 *   kluczTrasy  — klucz `ROUTE_MAP` (lustro pilnuje `constants/__tests__/routes.test.js`),
 *   etykieta    — nazwa miejsca na ekranie (≤ 30 znaków), zdanie — „co tu zrobisz” (jedno zdanie ≤ 90 znaków),
 *   ikona       — komponent react-icons,
 *   klucz       — uprawnienie obszaru (napis albo tablica „którykolwiek”); brak = klucz strony,
 *   wzor        — wzór widoku z 2.2 projektu (S1…S8 albo opis słowami z tabeli obszarów),
 *   widoki      — żetony widoku listy: [{ id, etykieta, licznik }] (≤ 4; wartości `?widok=`),
 *   filtry      — nazwy parametrów-filtrów tego obszaru (zostają po zamknięciu obiektu),
 *   obiekty     — [{ parametr, rodzaj, nazwa, dopelniacz, czesci, kroki, pokaz, dodatkowe, wymaga }] — obiekty w zapytaniu,
 *   kreatory    — [{ nazwa, tytul, kroki }] — wartości `?kreator=` (kroki razem z ostatnim „Sprawdź”, ≤ 5),
 *   pasek       — { szukajka, akcjaGlowna, akcjaPoZaznaczeniu, wiecej } — do rachunku szerokości paska (Z-02c),
 *   glowa       — { zdanieStanu, analityka } — prawa strona głowy listy (tylko obszary-listy),
 *   licznik     — czy miejsce ma licznik otwartych pozycji w rzędzie miejsc,
 *   slowa       — słowa do „Szukaj miejsca…”, także STARE nazwy zakładek,
 *   wkrotce     — funkcja nadchodząca: pozycja z tagiem, bez odnośnika,
 *   komponent   — leniwy import treści obszaru.
 */
export const obszar = (dane) => ({
    wzor: "S2",
    widoki: [],
    filtry: [],
    obiekty: [],
    kreatory: [],
    slowa: [],
    licznik: false,
    wkrotce: false,
    komponent: wBudowie,
    ...dane,
    pasek: { szukajka: null, akcjaGlowna: null, akcjaPoZaznaczeniu: null, wiecej: [], ...(dane.pasek || {}) },
});

/** Obszar startowy „Do załatwienia” — ten sam kształt na każdej stronie (wzór S1, szukanie miejsca w pasku). */
export const obszarStartu = (dane) => obszar({
    id: OBSZAR_STARTU,
    grupa: GRUPA_STARTU,
    segment: null,
    etykieta: ETYKIETA_STARTU,
    wzor: "S1",
    start: true,
    filtry: ["moje"],
    ...dane,
    pasek: { szukajka: "Szukaj miejsca…", ...(dane.pasek || {}) },
});

/**
 * Limity z zasady Z-02 projektu — jedno miejsce liczb dla zapadki N-2 (część rejestrowa) i dla klocków, które same ich pilnują.
 * Szerokość: treść ekranu odniesienia 1366 × 768 ma 73rem, rząd ma się zmieścić z zapasem 3rem.
 */
export const LIMITY = Object.freeze({
    grup: 5,
    obszarowWGrupie: 7,
    znakowEtykiety: 30,
    znakowZdania: 90,
    znakowDefinicji: 160,
    zetonow: 4,
    czesciObiektu: 8,
    grupCzesciProcedury: 5,
    czesciWGrupieProcedury: 4,
    krokowKarty: 7,
    krokowKreatora: 5,
    kontrolekPrawejStrefy: 4,
    znakowAkcjiGlownejZSzukajka: 18,
    znakowAkcjiGlownej: 24,
    szerokoscRzeduRem: 70,
});

/** Parametry zapytania o stałym znaczeniu na wszystkich czterech stronach (2.3 projektu, zapadka N-18). */
export const PARAMETRY = Object.freeze({
    /** Żeton widoku listy (filtr TEJ SAMEJ listy). */
    widok: "widok",
    /** Strona listy. */
    strona: "strona",
    /** Część otwartego obiektu. */
    czesc: "czesc",
    /** Żeton wewnątrz części obiektu. */
    pokaz: "pokaz",
    /** Krok kreatora albo karty z krokami. */
    krok: "krok",
    /** Nazwa otwartego kreatora (wartości pól nigdy nie trafiają do adresu). */
    kreator: "kreator",
    /** Otwarty słowniczek: `slownik`, `kto-co-robi` albo `gdzie-co-bylo`. */
    pojecia: "pojecia",
    /** Filtr spółki — ZAWSZE filtr (tak stoi w linkach powiadomień zapisanych w bazie), nigdy obiekt. */
    spolka: "spolka",
    /** Spółka jako obiekt (karta spółki, lista logowań spółki…). */
    karta: "karta",
    /** Przełącznik „Tylko moje” na liście „Do załatwienia”. */
    moje: "moje",
});

/** Sekcje słowniczka dozwolone w `?pojecia=`. */
export const SEKCJE_POJEC = Object.freeze(["slownik", "kto-co-robi", "gdzie-co-bylo"]);

/** Rodzaje obiektów otwieranych z obszaru — każdy rodzaj to jeden wzór widoku z rozdz. 2.2 projektu. */
export const RODZAJE_OBIEKTOW = Object.freeze([
    "karta",            // S5 — karta obiektu z częściami
    "karta-z-krokami",  // S8 — karta, której części są krokami o stanie z serwera
    "szuflada",         // S7 — szuflada pozycji nad listą
    "formularz",        // formularz 6–10 pól w miejscu treści
    "podglad",          // podgląd dokumentu albo pliku na pełnym ekranie
    // [mojdzial1006, MD-KIT, D-8] rodzaje Mojego Działu (opcja — cztery strony ich nie używają):
    "tresc",            // obiekt rysowany W MIEJSCU TREŚCI z „← Wróć do” (tablica `?tablica=ID`)
    "okno",             // cudze okno — zgłoszony wyjątek Z-15 (`KanbanTaskModal`, `?zadanieTablicy=ID`)
    "pelny-ekran",      // osobna trasa poza powłoką z identyfikatorem w ścieżce (aktywność osoby)
]);
